import { useMemo, useState } from "react";
import { useAdminAttendance, useAdminCompOffLedger, useHolidays } from "@/api/queries";
import { PageHeader } from "@/app/page-header";
import { AttendanceHeatmap, StatTile, StackedBarChart, TrendChart } from "@/components/charts";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { AttendanceLogDialog } from "@/pages/shared/attendance-log-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import { TBody, TD, TDPrimary, TH, THead, TR, TableWrap } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { attendanceRate, leaveDayCount, tallyByDay, tallyByTeam } from "@/lib/analytics";
import { MonthStepper } from "@/pages/shared/month-stepper";
import {
  classifyDay,
  currentMonth,
  eachDateInRange,
  holidayMap,
  isRestDay,
  leaveMap,
  monthBounds,
  todayStr,
  weekBounds,
  wfhMap,
} from "@/lib/calendar";
import { formatDate, formatDays, formatTime } from "@/lib/format";
import { formatMinutes } from "@/lib/org-time";
import { LEAVE_TYPE_SHORT, WORK_MODE_LABEL } from "@/lib/labels";
import type { LeaveDay } from "@/api/types";

type RangeKey = "today" | "week" | "month";

function rangeFor(key: RangeKey, month: string): { start: string; end: string; label: string } {
  const today = todayStr();
  if (key === "today") return { start: today, end: today, label: "Today" };
  if (key === "week") {
    const { start, end } = weekBounds(today);
    return { start, end, label: "This week" };
  }
  return { ...monthBounds(month), label: formatDate(`${month}-01`, "MMMM yyyy") ?? month };
}

export function HrAttendancePage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>("week");
  // Which month the Month tab shows. Starts on the current one; the arrows step
  // back through earlier months.
  const [month, setMonth] = useState(currentMonth());
  const [openPerson, setOpenPerson] = useState<{ id: number; name: string } | null>(null);

  const range = useMemo(() => rangeFor(rangeKey, month), [rangeKey, month]);
  const attendance = useAdminAttendance(range.start, range.end);
  // The today tiles and the roster are always about today. When the range being
  // browsed is an earlier month it holds nothing for today, so today is fetched
  // on its own rather than every row reading "No record".
  const today = todayStr();
  const rangeHasToday = range.start <= today && today <= range.end;
  const todayOnly = useAdminAttendance(today, today, !rangeHasToday);
  const todaySource = rangeHasToday ? attendance : todayOnly;
  const holidays = useHolidays();
  const compOff = useAdminCompOffLedger();

  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);
  const people = attendance.data?.users ?? [];
  const records = attendance.data?.records ?? [];
  // Live leave for the same range, indexed by person-day. Every figure and cell
  // below reads it, so a sanctioned absence stops reading as a no-show.
  const leave = useMemo(() => leaveMap(attendance.data?.leave), [attendance.data?.leave]);
  // Work from home for the same range, indexed the same way — and deliberately
  // NOT folded into `leave`. Every figure above is about absence; a work-from-
  // home day is a day worked, and counting it as leave would overstate the one
  // number HR is most likely to act on.
  const wfh = useMemo(() => wfhMap(attendance.data?.wfh), [attendance.data?.wfh]);

  const dates = useMemo(
    () => eachDateInRange(range.start, range.end),
    [range.start, range.end],
  );

  const dayTallies = useMemo(
    () => tallyByDay(people, records, range.start, range.end, holidayNames, leave),
    [people, records, range.start, range.end, holidayNames, leave],
  );

  const teamTallies = useMemo(
    () => tallyByTeam(people, records, range.start, range.end, holidayNames, leave),
    [people, records, range.start, range.end, holidayNames, leave],
  );

  const rate = attendanceRate(dayTallies);
  const leaveDays = leaveDayCount(dayTallies);

  // Today's snapshot, used for the today tiles and the roster table.
  const todayPeople = todaySource.data?.users ?? [];
  const todayRecords = todaySource.data?.records;
  const todayByUser = useMemo(() => {
    const map = new Map<number, (typeof records)[number]>();
    for (const record of todayRecords ?? []) if (record.date === today) map.set(record.user_id, record);
    return map;
  }, [todayRecords, today]);
  const todayLeave = useMemo(() => leaveMap(todaySource.data?.leave), [todaySource.data?.leave]);
  const todayWfh = useMemo(() => wfhMap(todaySource.data?.wfh), [todaySource.data?.wfh]);

  // "No record" now means exactly that: nothing recorded, and no approved leave
  // explaining it. Somebody on sanctioned leave used to be counted here, which
  // made this tile read as a problem when nothing was wrong.
  const notRecordedToday = todayPeople.filter(
    (person) =>
      !todayByUser.get(person.id)?.check_in &&
      todayLeave.get(`${person.id}|${today}`)?.status !== "approved" &&
      !isRestDay(today, new Set(holidayNames.keys())),
  ).length;

  const onLeaveToday = todayPeople.filter(
    (person) => todayLeave.get(`${person.id}|${today}`)?.status === "approved",
  ).length;

  const compOffEarned = (compOff.data ?? [])
    .filter((entry) => entry.type === "Earned")
    .reduce((sum, entry) => sum + entry.amount, 0);

  const trendData = dayTallies.map((tally) => ({
    day: formatDate(tally.date, "d MMM") ?? tally.date,
    complete: tally.complete,
    half: tally.half,
    leave: tally.leave,
    absent: tally.absent,
  }));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Org-wide"
        title="Attendance"
        sub="Check-in, check-out and full history for every account, in one place. HR never needs to open an individual employee's login to see this."
      />

      {/* One filter row above everything it scopes: all charts below re-render
          against the same slice. */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={rangeKey} onValueChange={(value) => setRangeKey(value as RangeKey)}>
          <TabsList>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="week">This week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
        {rangeKey === "month" && <MonthStepper month={month} onChange={setMonth} />}
        <span className="font-mono text-xs text-muted">
          {formatDate(range.start)} to {formatDate(range.end)}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatTile
          label="Accounts tracked"
          value={people.length}
          icon="team"
          loading={attendance.isLoading}
        />
        <StatTile
          label="Full days in range"
          value={rate === null ? "—" : `${rate}%`}
          icon="check-circle"
          loading={attendance.isLoading}
          meta={
            rate === null ? "Nothing recorded in this range" : "of working-day slots, leave excluded"
          }
        />
        <StatTile
          label="On leave today"
          value={onLeaveToday}
          icon="leave"
          loading={todaySource.isLoading || attendance.isLoading}
          meta={`${leaveDays} approved leave ${leaveDays === 1 ? "day" : "days"} in range`}
        />
        <StatTile
          label="No record today"
          value={notRecordedToday}
          icon="warning-circle"
          loading={todaySource.isLoading}
          meta={
            isRestDay(today, new Set(holidayNames.keys()))
              ? "Today is a weekly off"
              : "Working day, approved leave excluded"
          }
        />
        <StatTile
          label="Comp off earned"
          value={formatDays(compOffEarned)}
          icon="calendar-check"
          loading={compOff.isLoading}
          meta="Days credited, all time"
        />
      </div>

      <TrendChart
        title="Attendance across the range"
        note="Working days only. Weekly offs, company holidays and approved leave are excluded rather than counted as absence."
        data={trendData}
        xKey="day"
        series={[
          { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
          { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
          { key: "leave", label: "On leave", color: STATUS_SERIES_COLOR.leave },
          { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
        ]}
        loading={attendance.isLoading}
        {...(attendance.error ? { error: attendance.error.message } : {})}
        emptyBody="No working days with recorded attendance in this range."
      />

      <StackedBarChart
        title="Attendance by team"
        note="Person-days across the selected range"
        data={teamTallies as unknown as Record<string, string | number>[]}
        categoryKey="team"
        orientation="horizontal"
        series={[
          { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
          { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
          { key: "leave", label: "On leave", color: STATUS_SERIES_COLOR.leave },
          { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
        ]}
        loading={attendance.isLoading}
        {...(attendance.error ? { error: attendance.error.message } : {})}
        height={Math.max(200, teamTallies.length * 44)}
        emptyBody="No attendance recorded for any team in this range."
      />

      {rangeKey !== "today" && (
        <AttendanceHeatmap
          title="Every person, every day"
          note="One cell per person per day across the selected range"
          people={people.map((person) => ({ id: person.id, name: person.name }))}
          records={records}
          dates={dates}
          holidays={holidayNames}
          leave={leave}
          wfh={wfh}
          loading={attendance.isLoading}
          {...(attendance.error ? { error: attendance.error.message } : {})}
          emptyBody="No accounts to show."
        />
      )}

      <Panel>
        <PanelHeader
          title="Today's roster"
          note={`${formatDate(today)} · click any row for that person's full history`}
        />
        {todaySource.isLoading ? (
          <SkeletonTable rows={6} cols={5} />
        ) : todayPeople.length === 0 ? (
          <EmptyState
            icon="team"
            title="No accounts yet"
            body="Attendance appears here once HR has created employee or manager accounts."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Team</TH>
              <TH>Mode</TH>
              <TH>Check in</TH>
              <TH>Check out</TH>
              <TH numeric>Hours</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">History</span>
              </TH>
            </THead>
            <TBody>
              {todayPeople.map((person) => {
                const record = todayByUser.get(person.id);
                const onLeave = todayLeave.get(`${person.id}|${today}`);
                const fromHome = todayWfh.get(`${person.id}|${today}`);
                const kind = classifyDay(today, record, holidayNames, onLeave, fromHome);
                // The day's TOTAL across its sessions. Not last-out minus
                // first-in, which spans the breaks once a day has more than one
                // session, and not mode-dependent — every mode is judged on hours.
                const minutes = record?.total_minutes ?? 0;
                return (
                  <TR key={person.id}>
                    <TDPrimary>{person.name ?? "Unnamed"}</TDPrimary>
                    <TD>{person.team_name ?? "Unassigned"}</TD>
                    <TD>
                      <span className="flex flex-wrap items-center gap-1.5">
                        {record?.mode
                          ? WORK_MODE_LABEL[record.mode]
                          : person.work_mode
                            ? WORK_MODE_LABEL[person.work_mode]
                            : "—"}
                        {/* Approved work from home today — shown whether or not
                            they have clocked in yet. Before they do, it is the
                            only thing on the row that explains an empty check-in;
                            after, it says the remote day was agreed in advance. */}
                        {fromHome?.status === "approved" && (
                          <Badge tone="accent" icon="wfh">
                            WFH
                          </Badge>
                        )}
                      </span>
                    </TD>
                    <TD className="font-mono text-xs">{formatTime(record?.check_in ?? null) ?? "—"}</TD>
                    <TD className="font-mono text-xs">{formatTime(record?.check_out ?? null) ?? "—"}</TD>
                    <TD numeric className="font-mono text-xs">
                      {minutes > 0 ? formatMinutes(minutes) : "—"}
                    </TD>
                    <TD>
                      <AttendanceStatusBadge kind={kind} leave={onLeave} />
                    </TD>
                    <TD>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpenPerson({ id: person.id, name: person.name ?? "Unnamed" })}
                      >
                        History
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      <Panel>
        <PanelHeader
          title="Comp off ledger"
          note="Credited automatically when someone works a weekly off or a company holiday"
        />
        {compOff.isLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : (compOff.data ?? []).length === 0 ? (
          <EmptyState
            icon="calendar-check"
            title="No comp off earned yet"
            body="Nine hours or more on a rest day credits a full day, 4.5 to 9 hours credits half, and less than 4.5 hours credits nothing."
          />
        ) : (
          <TableWrap>
            <THead>
              <TH>Person</TH>
              <TH>Date</TH>
              <TH>Entry</TH>
              <TH numeric>Days</TH>
              <TH>Reason</TH>
            </THead>
            <TBody>
              {(compOff.data ?? []).slice(0, 25).map((entry, index) => (
                <TR key={`${entry.employee_name}-${entry.date}-${index}`}>
                  <TDPrimary>{entry.employee_name ?? "Unnamed"}</TDPrimary>
                  <TD className="font-mono text-xs">{formatDate(entry.date)}</TD>
                  <TD>
                    <Badge tone={entry.type === "Earned" ? "good" : "neutral"}>{entry.type}</Badge>
                  </TD>
                  <TD numeric>{entry.amount}</TD>
                  <TD className="max-w-[28ch] truncate">{entry.reason}</TD>
                </TR>
              ))}
            </TBody>
          </TableWrap>
        )}
      </Panel>

      {openPerson && (
        <AttendanceLogDialog
          scope="admin"
          person={openPerson}
          onClose={() => setOpenPerson(null)}
          holidays={holidayNames}
        />
      )}
    </div>
  );
}

/**
 * The day's status, as one badge.
 *
 * `leave` is passed separately from `kind` because the two answer different
 * questions and only sometimes the same one. When the day has no attendance on
 * it, the day IS the leave and the badge says so. When the day was worked,
 * `kind` wins — they turned up — and the leave becomes a second, quieter badge
 * beside it, because a worked day that was also booked as leave is the one
 * combination somebody actually has to go and reconcile.
 */
export function AttendanceStatusBadge({
  kind,
  leave,
}: {
  kind: ReturnType<typeof classifyDay>;
  leave?: LeaveDay | undefined;
}) {
  const clash =
    leave && (kind === "complete" || kind === "half") ? (
      <Badge tone="outline" icon="leave">
        {leave.status === "approved" ? "Booked as leave" : "Leave requested"}
      </Badge>
    ) : null;

  const primary =
    kind === "complete" ? (
      <Badge tone="good" icon="check-circle">Full day</Badge>
    ) : kind === "half" ? (
      <Badge tone="warn" icon="warning-circle">Half day</Badge>
    ) : kind === "leave" ? (
      <Badge tone="accent" icon="leave">
        {leave ? `On leave · ${LEAVE_TYPE_SHORT[leave.leave_type] ?? leave.leave_type}` : "On leave"}
      </Badge>
    ) : kind === "leave-pending" ? (
      <Badge tone="warn" icon="leave">Leave pending</Badge>
    ) : kind === "wfh" ? (
      /* An UPCOMING day approved to be worked from home. Outline rather than a
         filled tone, matching "Upcoming" below: nothing has happened yet, and a
         solid badge would read as a state the day is already in. */
      <Badge tone="outline" icon="wfh">Work from home</Badge>
    ) : kind === "inactive" ? (
      <Badge tone="neutral" icon="x-circle">Day off</Badge>
    ) : kind === "absent" ? (
      <Badge tone="bad" icon="x-circle">No record</Badge>
    ) : kind === "holiday" ? (
      <Badge tone="neutral" icon="holiday">Holiday</Badge>
    ) : kind === "rest" ? (
      <Badge tone="neutral" icon="holiday">Weekly off</Badge>
    ) : (
      <Badge tone="outline">Upcoming</Badge>
    );

  if (!clash) return primary;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {primary}
      {clash}
    </span>
  );
}
